import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { ContentType, AdminRole, Content } from '@/types/custom';
import { CONTENT_TYPES } from '@/lib/constants';

export interface ContentTypeStat {
  plays: number;
  dur_sec: number;
}

export interface PlayStats {
  types: Partial<Record<ContentType, ContentTypeStat>>;
  topPlayed: Array<TopPlay>;
  activityDates: Date[];
  activeUsers: number;
}

export interface TopPlay {
  content: Content;
  plays: number;
  dur_sec: number;
}

export interface PlayFilters {
  startTime?: Date;
  endTime?: Date;
  excludeAdmins?: boolean;
  userId?: string;
  userIds?: string[];
  disabled?: boolean; // wait to search (shows 0 results)
}

/**
 * Read activity stats on content plays.
 */
export function useContentPlays(filters: PlayFilters) {
  const [contentPlays, setContentPlays] = useState<PlayStats>({
    types: {},
    topPlayed: [],
    activityDates: [],
    activeUsers: 0,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadContentPlays();
  }, [
    filters.startTime,
    filters.endTime,
    filters.excludeAdmins,
    filters.userId,
    filters.userIds?.join(','),
    filters.disabled,
  ]);

  async function loadContentPlays() {
    if (filters.disabled) {
      return setContentPlays({
        types: {},
        topPlayed: [],
        activityDates: [],
        activeUsers: 0,
      });
    }

    // const {
    //   data: { user },
    // } = await supabase.auth.getUser();
    // if (!user) return;

    // Get activities with content information

    // Build query with filters
    let query = supabase
      .from('activity')
      .select(
        '*, content_id, content(*), updated_at, user_id, profiles!left(id, profile_role!left(role))',
      )
      .not('content_id', 'is', null);

    // apply filters
    if (filters.userIds && filters.userIds.length > 0) {
      query = query.in('user_id', filters.userIds);
    } else if (filters.userId) {
      query = query.eq('user_id', filters.userId);
    }
    if (filters.startTime) {
      query = query.gte('updated_at', filters.startTime.toISOString());
    }
    if (filters.endTime) {
      query = query.lte('updated_at', filters.endTime.toISOString());
    }

    setLoading(true);
    const { data: activities, error } = await query;
    setLoading(false);

    if (error) {
      console.error('Error fetching content plays:', error);
      return;
    }

    if (!activities) return;

    // Filter out admin users if requested (client-side since DB filtering on nested arrays is complex)
    let filteredActivities = activities;
    if (filters.excludeAdmins) {
      filteredActivities = activities.filter((activity: any) => {
        // profile_role is an array, get the first element
        const profileRoles = activity.profiles?.profile_role;
        if (!profileRoles || profileRoles.length === 0) {
          // No role assigned, include the activity
          return true;
        }
        const role = profileRoles[0]?.role;
        return role !== AdminRole;
      });
    }

    // Count plays and duration by content type
    const counts = filteredActivities.reduce(
      (acc, activity) => {
        const content = activity.content as any;
        if (content?.content_type) {
          if (!acc[content.content_type]) {
            acc[content.content_type] = { plays: 0, dur_sec: 0 };
          }
          acc[content.content_type].plays += 1;
          acc[content.content_type].dur_sec += activity.dur_sec;
        }
        return acc;
      },
      {} as Record<string, ContentTypeStat>,
    );

    // Get top played content (by number of activity records)
    const playCounts: Record<string, TopPlay> = filteredActivities.reduce(
      (acc, activity) => {
        const content = activity.content as Content;
        if (content?.id) {
          if (!acc[content.id]) {
            acc[content.id] = {
              content: content,
              plays: 0,
              dur_sec: 0,
            } satisfies TopPlay;
          }
          acc[content.id].plays += 1;
          acc[content.id].dur_sec += activity.dur_sec;
        }
        return acc;
      },
      {} as Record<string, TopPlay>,
    );

    const topPlayed = Object.values(playCounts)
      .sort((a, b) => {
        if (b.plays !== a.plays) {
          return b.plays - a.plays;
        }
        return b.dur_sec - a.dur_sec;
      })
      .slice(0, 10);

    // Get unique activity dates
    const activityDates = filteredActivities
      .map((a) => new Date(a.updated_at))
      .filter(
        (date, index, self) =>
          self.findIndex((d) => d.toDateString() === date.toDateString()) ===
          index,
      )
      .sort((a, b) => b.getTime() - a.getTime());

    // Map to all content types (including those with 0 plays)
    const byType = Object.keys(CONTENT_TYPES).reduce(
      (acc, type) => {
        acc[type as ContentType] = counts[type] || { plays: 0, dur_sec: 0 };
        return acc;
      },
      {} as Partial<Record<ContentType, ContentTypeStat>>,
    );

    const activeUsers = new Set(filteredActivities.map((a) => a.user_id)).size;

    setContentPlays({
      types: byType,
      topPlayed,
      activityDates,
      activeUsers,
    });
  }

  return { contentPlays, loading };
}
